import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Pill,
  SvgIcon,
  SvgNames,
  Typography,
  tokens,
} from "@sunrun/experience-ui-components";
import styled from "@emotion/styled/macro";
import { isSunrunManaged } from "@sunrun/sales-experience-shared";
import { useFlags } from "flagsmith/react";
import { TaskItem } from "../molecules/TaskItem";
import { DownloadCustomerApp } from "../atoms/DownloadCustomerApp";
import { CheckoutContractReviewIssuesModal } from "../molecules/CheckoutContractReviewIssuesModal";
import type { ContractReviewMessage } from "../../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";
import { HorizontalLine } from "components/atoms/HorizontalLine";
import { useAuthToken } from "hooks/useAuthToken";
import { Box } from "components/atoms/Box";
import {
  filterKnownContractReviewMessages,
  getFirstTaskIdForOrderedContractReviewMessages,
  hasMessagesForStep,
} from "utils/contractReviewUtils";
import { FeatureFlags } from "fixtures/features";

type TaskListProps = {
  title: string;
  installationPartner?: string;
  isMobile?: boolean;
  isSRM?: boolean;
};

const TaskList: React.FC<TaskListProps> = ({
  title,
  installationPartner,
  isMobile,
}) => {
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const {
    tasks,
    navigateToTask,
    userProfile,
    currentTaskId,
    contractReviewMessages,
    isContractReviewLoading,
    isContractReviewInProgress,
  } = useCheckoutContext();
  const isSRM = userProfile && isSunrunManaged(userProfile);
  const { pathname } = useLocation();
  const isCustomer = !!proposalToken;
  const flags = useFlags([FeatureFlags.CONTRACT_REVIEW]);
  const isContractReviewEnabled =
    flags[FeatureFlags.CONTRACT_REVIEW]?.enabled ?? false;

  const contractReviewIssuesMessages = useMemo(
    () => filterKnownContractReviewMessages(contractReviewMessages),
    [contractReviewMessages]
  );

  const [contractReviewModalOpen, setContractReviewModalOpen] = useState(false);
  const [modalMessages, setModalMessages] = useState<ContractReviewMessage[]>(
    []
  );
  const [pillNavigationTargetTaskId, setPillNavigationTargetTaskId] = useState<
    string | null
  >(null);

  const openContractReviewIssuesModal = useCallback(() => {
    if (contractReviewIssuesMessages.length === 0) {
      return;
    }
    setModalMessages(contractReviewIssuesMessages);
    setContractReviewModalOpen(true);
  }, [contractReviewIssuesMessages]);

  /* When the user clicks the issues pill chevron, we need to determine which task to navigate to. */
  const handleContractReviewPillAction = useCallback(() => {
    if (!isContractReviewEnabled) {
      return;
    }
    const firstTaskId = getFirstTaskIdForOrderedContractReviewMessages(
      contractReviewIssuesMessages,
      tasks
    );

    if (!firstTaskId || firstTaskId === currentTaskId) {
      return openContractReviewIssuesModal();
    }

    const targetTask = tasks.find((task) => task.id === firstTaskId);
    if (!targetTask?.isUnlocked || !targetTask.urlSlug) {
      return openContractReviewIssuesModal();
    }

    setPillNavigationTargetTaskId(firstTaskId);
    navigateToTask(firstTaskId);
  }, [
    isContractReviewEnabled,
    contractReviewIssuesMessages,
    tasks,
    currentTaskId,
    navigateToTask,
    openContractReviewIssuesModal,
  ]);

  useEffect(() => {
    if (pillNavigationTargetTaskId == null) {
      return;
    }
    if (pillNavigationTargetTaskId !== currentTaskId) {
      return;
    }
    openContractReviewIssuesModal();
    setPillNavigationTargetTaskId(null);
  }, [
    pillNavigationTargetTaskId,
    currentTaskId,
    openContractReviewIssuesModal,
  ]);

  const handleContractReviewAcknowledge = useCallback(() => {
    setModalMessages([]);
    setContractReviewModalOpen(false);
  }, []);

  const contractReviewStatusPill = useMemo(() => {
    if (isContractReviewLoading) {
      return <StyledPill text="Checking for automated review" type="info" />;
    }
    if (isContractReviewInProgress) {
      return <StyledPill text="Automated review in progress" type="info" />;
    }
    if (contractReviewIssuesMessages.length > 0) {
      return (
        <StyledContractReviewIssuesPill type="warning" hideIcon>
          <IssuesWarningPillLayout>
            <IssuesPillWarningGlyph aria-hidden>
              <SvgIcon
                name={SvgNames.WarningFilled}
                color={tokens.WARNING_20}
                height={16}
                width={16}
              />
            </IssuesPillWarningGlyph>
            <IssuesPillMessage>
              {contractReviewIssuesMessages.length} issue
              {contractReviewIssuesMessages.length > 1 ? "s" : ""} found, after
              editing{" "}
              <IssuesPillRefreshLink
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  window.location.reload();
                }}
              >
                refresh the page
              </IssuesPillRefreshLink>
            </IssuesPillMessage>
            <IssuesPillChevronButton
              type="button"
              data-testid="contract-review-issues-pill-action"
              aria-label="View issue details"
              onClick={handleContractReviewPillAction}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleContractReviewPillAction();
                }
              }}
            >
              <SvgIcon
                name={SvgNames.ChevronRight}
                color={tokens.WARNING_20}
                height={20}
                width={20}
              />
            </IssuesPillChevronButton>
          </IssuesWarningPillLayout>
        </StyledContractReviewIssuesPill>
      );
    }
    return <StyledPill text="Automated review was successful" type="success" />;
  }, [
    isContractReviewLoading,
    isContractReviewInProgress,
    contractReviewIssuesMessages,
    handleContractReviewPillAction,
  ]);

  const issuesModal = isContractReviewEnabled ? (
    <CheckoutContractReviewIssuesModal
      messages={modalMessages}
      onAcknowledge={handleContractReviewAcknowledge}
      open={contractReviewModalOpen}
    />
  ) : null;

  if (isMobile) {
    return (
      <>
        <StyledHorizontalLine />
        {isContractReviewEnabled && contractReviewStatusPill}
        <Typography size={tokens.FONT_SIZE_0} color={tokens.TEXT_LIGHT}>
          Complete the following tasks to finish your solar offer.
        </Typography>
        <StyledMobileNav>
          <NavItemsContainer>
            {tasks.map((task, index) => (
              <StyledTaskItem
                isFirst={index === 0}
                isLast={index === tasks.length - 1}
                isMobile={isMobile}
                className="task-item"
                {...task}
                key={task.id}
                onClick={() => navigateToTask(task.id)}
                isSelected={!!(task.urlSlug && pathname.includes(task.urlSlug))}
                isCustomer={isCustomer}
                hasWarning={hasMessagesForStep(
                  contractReviewIssuesMessages,
                  task.id
                )}
              />
            ))}
          </NavItemsContainer>
        </StyledMobileNav>
        {issuesModal}
      </>
    );
  }

  return (
    <>
      <nav>
        <Header>
          <Typography variant="h5" color={tokens.BLACK}>
            {title}
          </Typography>
          {installationPartner && (
            <Typography size="small" color={tokens.TINTS_OFF_WHITE_10}>
              {installationPartner}
            </Typography>
          )}
        </Header>
        {isContractReviewEnabled && contractReviewStatusPill}
        {tasks.map((task) => (
          <TaskItem
            {...task}
            key={task.id}
            onClick={() => navigateToTask(task.id)}
            isSelected={!!(task.urlSlug && pathname.includes(task.urlSlug))}
            isCustomer={isCustomer}
            hasWarning={hasMessagesForStep(
              contractReviewIssuesMessages,
              task.id
            )}
          />
        ))}
        {isSRM && (
          <Box marginTop="15px" marginBottom="15px">
            <DownloadCustomerApp isCompactSize />
          </Box>
        )}
      </nav>
      {issuesModal}
    </>
  );
};

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 8px;
  border-radius: 8px;
  background-color: ${tokens.TINTS_HERO_BLUE_90};
  margin-bottom: 16px;
`;

/** Overrides default Pill `white-space: pre`, tight padding, and vertical centering that break multi-line content. */
const StyledContractReviewIssuesPill = styled(Pill)`
  margin-bottom: 16px;
  && {
    width: 100%;
    min-height: auto;
    box-sizing: border-box;
    align-items: center;
    justify-content: flex-start;
    padding: 10px 12px;
    gap: 0;
    white-space: normal;
    overflow: visible;
  }
`;

const IssuesWarningPillLayout = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  padding-right: 28px;
  box-sizing: border-box;
  overflow: hidden;
`;

const IssuesPillWarningGlyph = styled.div`
  float: left;
  display: flex;
  align-items: center;
  margin-right: 4px;
  margin-top: 1px;
  height: 16px;
`;

/** Floated icon on line 1, wrapped lines span full width (under the icon) */
const IssuesPillMessage = styled.p`
  margin: 0;
  color: ${tokens.WARNING_20};
  line-height: 1.35;
  text-align: left;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`;

const IssuesPillRefreshLink = styled.button`
  display: inline;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  color: ${tokens.WARNING_20};
  font-family: "Roobert";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;

  &:focus-visible {
    outline: 2px solid ${tokens.WARNING_20};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const IssuesPillChevronButton = styled.button`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 2px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${tokens.WARNING_20};
  line-height: 0;
  min-width: 28px;
  z-index: 1;

  &:focus-visible {
    outline: 2px solid ${tokens.WARNING_20};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const StyledPill = styled(Pill)`
  margin-bottom: 16px;
  && {
    width: 100%;
  }
`;

const StyledHorizontalLine = styled(HorizontalLine)`
  width: 100%;
  margin: auto 0;
  border-color: ${tokens.BORDER_LIGHT};
  margin-bottom: 16px;
`;

const StyledTaskItem = styled(TaskItem)<{
  isFirst: boolean;
  isLast: boolean;
  isCustomer: boolean;
}>`
  display: flex;
  padding-top: 16px;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  min-width: 80px;
  ${({ isFirst }) =>
    isFirst &&
    `
    margin-left: -16px;
    /* needed to offset the margin of the first task item in mobile view */
  `}
  ${({ isLast }) =>
    isLast &&
    `
    margin-right: 16px;
    /* needed to offset the margin of the last task item in mobile view */
  `}
  ${({ isCustomer }) =>
    isCustomer &&
    `
    max-width: 90px;
  `}

  > p {
    text-align: center;
    font-size: 14px;
    line-height: 16px;
  }
`;

const NavItemsContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledMobileNav = styled.nav`
  display: flex;
  overflow: scroll;
`;

export { TaskList };
export type { TaskListProps };
